import { getPollingCtrl } from './pollingController';
import { useContext, useState } from 'react';
import api from '../api/api';
import { Case } from '../models/common-types';
import constants from '../constants/constants';
import { getParam, updateParam } from '../utils/communication';
import { findCaseFromRoot } from '../utils/find';
import { i18n } from '../i18n/i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { NOTIFICATION, useNotifications } from '@cellebrite/saas-ui-common';
import { AuthContext } from './authContext';

export interface CaseDto {
    name?: string;
    description?: string;
    parentId?: string;
}

export default function useCases(wsId: number | null) {
    const {
        user: { id: userId },
    } = useContext(AuthContext);
    const [showMessage] = useNotifications();
    const [activeCaseId, setActiveCaseId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const pollintCtrl = getPollingCtrl();

    const { data: cases, isLoading } = useQuery(
        ['cases', wsId],
        ({ queryKey }) => api.cases.fetchCases(queryKey[1] as number),
        {
            enabled: !!wsId,
            refetchInterval: !pollintCtrl.stopCasePollingInfo ? constants.CASES_POLLING_DELAY : false,
            onSuccess: (casesFromApi) => {
                if (!activeCaseId || casesFromApi?.length === 1) {
                    const paramCaseId = getParam('caseId');
                    const storedCaseId = localStorage.getItem(`id_${userId}${wsId}_caseId`);
                    const foundCase = findCaseFromRoot(casesFromApi, paramCaseId || storedCaseId);
                    const activeCase = foundCase?.id || casesFromApi?.[0]?.id;
                    setActiveCaseId(activeCase);
                    updateParam('caseId', activeCase);
                }
            },
        },
    );

    const setActiveCase = (caseId: string | null) => {
        if (caseId) {
            updateParam('caseId', caseId);
            localStorage.setItem(`id_${userId}${wsId}_caseId`, caseId);
            setActiveCaseId(caseId);
        } else {
            setActiveCaseId(null);
        }
    };

    const createCase = useMutation(
        (createObj: { pendoEventName: string; caseData: CaseDto }) => api.cases.createCase(wsId, createObj.caseData),
        {
            onSuccess: (_res, createObj) => {
                showMessage(
                    i18n.translate('CASE_CREATION_SUCCESS', { name: createObj.caseData.name }),
                    NOTIFICATION.TYPE.SUCCESS,
                );
                queryClient.invalidateQueries('cases');
            },
            onError: (_res, createObj) =>
                showMessage(
                    i18n.translate('CASE_CREATION_ERROR', { name: createObj.caseData.name }),
                    NOTIFICATION.TYPE.ERROR,
                ),
        },
    );

    const updateCase = useMutation(
        (reqParams: { caseId: string; caseData: CaseDto }) =>
            api.cases.updateCase(wsId, reqParams.caseId, reqParams.caseData),
        {
            onSuccess(_res, { caseData }) {
                showMessage(
                    i18n.translate('CASE_UPDATE_SUCCESS', { caseName: caseData.name }),
                    NOTIFICATION.TYPE.SUCCESS,
                );
                queryClient.invalidateQueries('cases');
            },
            onError: (_res, { caseData }) =>
                showMessage(
                    i18n.translate('CASE_UPDATE_ERROR', { caseName: caseData.name }),
                    NOTIFICATION.TYPE.SUCCESS,
                ),
        },
    );

    const deleteCase = useMutation((caseNode: Case) => api.cases.deleteCase(wsId, caseNode.id), {
        onSuccess(_res, { name }) {
            showMessage(i18n.translate('CASE_DELETION_SUCCESS', { name }), NOTIFICATION.TYPE.SUCCESS);
            queryClient.invalidateQueries('cases');
        },
        onError: (_res, { name }) =>
            showMessage(i18n.translate('CASE_DELETION_ERROR', { name }), NOTIFICATION.TYPE.ERROR),
    });

    const moveCase = useMutation(
        (reqParams: { caseNode: Case; destination: string }) =>
            api.cases.updateCase(wsId, reqParams.caseNode.id, { parentId: reqParams.destination }),
        {
            onSuccess(_res, { caseNode }) {
                showMessage(i18n.translate('CASE_MOVED_SUCCESS', { name: caseNode.name }), NOTIFICATION.TYPE.SUCCESS);
                queryClient.invalidateQueries('cases');
            },
            onError: (_res, { caseNode }) =>
                showMessage(i18n.translate('CASE_MOVED_ERROR', { name: caseNode.name }), NOTIFICATION.TYPE.ERROR),
        },
    );

    return {
        cases,
        activeCaseId,
        isLoading,
        setActiveCase,
        deleteCase: deleteCase.mutate,
        updateCase: updateCase.mutate,
        createCase: createCase.mutate,
        moveCase: moveCase.mutate,
    };
}
